import { Injectable } from "@angular/core";
import { ApiService } from "../api.service";
import { take, takeUntil } from "rxjs";
import { StorageService } from "../storage.service";
import { IPeriodPaymentPlanInfo } from "../../models/payment/period-payment-plan-info.model";

@Injectable()
export class PeriodPlanService {
  private apiPrefix = '/client/period-plan';
  private readonly TEMPORARY_KEY = '__temporaryPeriod';

  constructor(
    private api: ApiService,
    private storageService: StorageService
  ) {
  }

  getListedPeriodAndPrices() {
    return this.api.get(`${this.apiPrefix}`);
  }

  getSelectedPeriodId() {
    const selectedPeriodId = this.storageService.getItem('selectedPeriodId', false);
    if (selectedPeriodId) {
      return Number(selectedPeriodId);
    }

    return undefined;
  }

  setSelectedPeriodId(val: any) {
    this.storageService.setItem('selectedPeriodId', val, false);
  }

  /**
   * Temporarily hides the selected period by caching it in local storage,
   * and clears the current selected period.
   */
  cacheAndClearSelectedPeriod(): void {
    const selectedPeriod = this.getSelectedPeriodId();
    if (!selectedPeriod) return;

    this.storageService.setItem(this.TEMPORARY_KEY, selectedPeriod);
    this.removeSelectedPeriodId();
  }

  /**
   * Restores the temporarily cached period from storage.
   *
   * If a period is already selected:
   * - If `overwrite` is true, it replaces the selected period with the cached one.
   * - If `overwrite` is false, it keeps the current period.
   *
   * If no period is selected, the cached one is restored directly.
   *
   * @param overwrite - Whether to overwrite the existing selected period if one exists.
   */
  restoreCachedPeriod(overwrite: boolean = false): void {
    const temporaryPeriod = this.storageService.getItem(this.TEMPORARY_KEY, false);
    if (!temporaryPeriod) return;

    const selectedPeriod = this.getSelectedPeriodId();

    const shouldRestore = !selectedPeriod || overwrite;

    if (shouldRestore) {
      this.setSelectedPeriodId(temporaryPeriod);
      this.storageService.removeItem(this.TEMPORARY_KEY);
    }
  }

  /**
   * Clear temporary period item from the local storage
   */
  clearCachedPeriod(): void {
    this.storageService.removeItem(this.TEMPORARY_KEY)
  }

  removeSelectedPeriodId() {
    this.storageService.removeItem('selectedPeriodId');
  }

  setAutoSelectedPeriod() {
    this.getListedPeriodAndPrices().pipe(take(1))
      .subscribe(r => {
        const periodPriceInfo = r.data as IPeriodPaymentPlanInfo;
        const selectedPeriod = this.getSelectedPeriodId();
        if (!(selectedPeriod && periodPriceInfo.userPeriodIds.includes(selectedPeriod))) {
          let selectPeriodId;

          if (periodPriceInfo.userPeriodIds.length === 1) {
            selectPeriodId = periodPriceInfo.userPeriodIds[0];
          }

          if (!selectPeriodId) {
            const activePeriod = periodPriceInfo.userPeriodIds.find(a => a == periodPriceInfo.activePeriod.id);
            if (activePeriod) {
              selectPeriodId = activePeriod;
            }
          }

          if (!selectPeriodId) {
            selectPeriodId = periodPriceInfo.userPeriodIds[periodPriceInfo.userPeriodIds.length - 1];
          }

          this.setSelectedPeriodId(selectPeriodId);
          this.clearCachedPeriod();
        }

      });
  }
}
